import {
    VerifiedUser,
    HistoryEdu
} from "@mui/icons-material";
import {
    Typography,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Box,
    CircularProgress,
    Alert,
    Paper,
    Divider,
    Chip
} from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import {
    getSignatureHistoryEnvelopes,
    getSignatureStatusColor,
    mapSignatureHistory
} from "./signature.utils";
import { useGetSignerResultHistory } from "../../../../../api/bpm/endpoints";

export const Route = createFileRoute('/__auth/processes/$processInstanceId/history/signature')({
    component: SignaturePage,
    staticData: {
        title: "routes.processSignatures",
    },
});

function SignaturePage() {
    const { t } = useTranslation();
    const { processInstanceId } = Route.useParams();

    const { data: signerHistoryResponse, isLoading, error } = useGetSignerResultHistory(processInstanceId);

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error">{t("processes.history.signaturesError")}</Alert>
            </Box>
        );
    }

    const displayData = mapSignatureHistory(
        getSignatureHistoryEnvelopes(signerHistoryResponse)
    );

    if (!isLoading && (!displayData || displayData.length === 0)) {
        return (
            <Paper elevation={0} sx={{ mt: 2, border: '1px solid', borderColor: 'divider', p: 4, textAlign: 'center' }}>
                <HistoryEdu sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">{t("processes.history.noSignatures")}</Typography>
            </Paper>
        );
    }

    return (
        <Paper elevation={0} sx={{ mt: 2, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6">{t("processes.history.signaturesTitle")}</Typography>
            </Box>
            <List>
                {displayData.map((signer, index) => (
                    <div key={signer.id}>
                        <ListItem>
                            <ListItemAvatar>
                                <Avatar sx={{ bgcolor: 'primary.light' }}>
                                    <HistoryEdu />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={signer.name || t("common.user")}
                                secondary={
                                    <>
                                        <Typography component="span" variant="body2" color="text.primary">
                                            {signer.date ? new Date(signer.date).toLocaleString() : '-'}
                                        </Typography>
                                        <br />
                                        {signer.envelopeTitle && `${signer.envelopeTitle} · `}
                                        {signer.document && `${signer.documentType || 'Documento'}: ${signer.document}`}
                                    </>
                                }
                            />
                            <Box>
                                <Chip
                                    label={signer.status || t("signatures.status.signed")}
                                    color={getSignatureStatusColor(signer.status)}
                                    size="small"
                                    icon={<VerifiedUser />}
                                />
                            </Box>
                        </ListItem>
                        {index < displayData.length - 1 && <Divider variant="inset" component="li" />}
                    </div>
                ))}
            </List>
        </Paper>
    );
}
